import React, { useState, useEffect } from 'react';
import { validateIntentName } from '@paskills/validation';
import { reachGoal } from '../../lib/metrika';
import {
    GrammarIntentTestResult,
    GrammarValidationErrorPayload,
    GrammarIntentEditableFields,
} from '../../model/grammar';
import { useFetchingHandler } from '../../hooks/useFetchingHandler';
import { useGrammarPageContext } from '../../context/grammarPageContext';
import { useCurrentAliceApp } from '../../hooks/useCurrentAliceApp';
import { GrammarPageIntentSettings } from '../GrammarPageIntentSettings/GrammarPageIntentSettings';
import { GrammarPageVerstehen } from '../GrammarPageVerstehen/GrammarPageVerstehen';
import { granetApi, ApiError } from '../../api';
import { useFormSettings } from '../../hooks/useFormSettings';
import { useSettingsPageContext } from '../../context/settingsPageContext';

type ShowingPage = 'IntentPage' | 'VerstehenPage' | 'loading';

interface Props {
    intentId: string;
    onDelete: () => void;
    readOnly?: boolean;
    onClose: () => void;
    customEntitiesNames: string[];
    isChanged: boolean;
    setIsChanged: (isChanged: boolean) => void;
    intentSettings: GrammarIntentEditableFields;
    showingPage?: ShowingPage;
    skillId: string;
    entities: string;
}
export const GrammarPageIntentModule: React.FC<Props> = ({
    intentId,
    onDelete,
    readOnly,
    onClose,
    customEntitiesNames,
    isChanged,
    setIsChanged,
    intentSettings,
    skillId,
    entities,
}) => {
    const [currentPage, setCurrentPage] = useState<ShowingPage>('IntentPage');
    const onVerstehenHandler = async () => {
        setCurrentPage('VerstehenPage');
        reachGoal('verstehenLaunch');
    };
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const skill = useCurrentAliceApp()!;
    const [testResults, setTestResults] = useState<GrammarIntentTestResult>();
    const { isEditable } = useSettingsPageContext();
    const { settings, setSettings, onChange, errors, hasErrors } = useFormSettings({
        defaultSettings: intentSettings,
        readOnly: !isEditable,
        registerChanges: setIsChanged,
        validators: {
            formName: (value) => value && validateIntentName(value),
        },
    });
    const onIntentHandler = (pos: string, neg: string) => {
        setCurrentPage('IntentPage');
        setSettings({ ...settings, negativeTests: neg, positiveTests: pos });
        setIsChanged(true);
    };
    const { refreshIntents } = useGrammarPageContext();
    const [validationError, setValidationError] = useState<GrammarValidationErrorPayload | null>(null);
    const onSave = async () => {
        setValidationError(null);

        const { validationError } = await granetApi.updateGrammarIntent({
            skillId: skill.id,
            channel: skill.channel,
            intentId,
            intentSettings: settings,
        });

        setValidationError(validationError);

        await refreshIntents();

        setIsChanged(false);
    };
    const [onSaveHandler, saveState, initSaveState] = useFetchingHandler({
        throwErrors: true,
        handler: onSave,
    });
    const [onTestHandler, testState, initTestState] = useFetchingHandler({
        throwErrors: true,
        omitResult: true,
        handler: async () => {
            if (isChanged) {
                await onSave();
            }

            return granetApi.runGrammarIntentTests({ skillId: skill.id, intentId }, skill.channel);
        },
        onLoad: (res) => {
            setTestResults(res);
        },
        onError: (e) => {
            if (e instanceof ApiError && e.isGrammarValidationError()) {
                setValidationError(e.payload ?? null);
            }
        },
    });
    const [onDeleteHandler, deleteState, initDeleteState] = useFetchingHandler({
        throwErrors: true,
        handler: async () => {
            await granetApi.deleteGrammarIntent({ skillId: skill.id, intentId });
            onDelete();
        },
    });
    useEffect(() => {
        initSaveState();
        initTestState();
        initDeleteState();
    }, [initDeleteState, initSaveState, initTestState]);
    return currentPage === 'IntentPage' ? (
        <GrammarPageIntentSettings
            readOnly={readOnly}
            onClose={onClose}
            customEntitiesNames={customEntitiesNames}
            onShow={onVerstehenHandler}
            onTestHandler={onTestHandler}
            onDeleteHandler={onDeleteHandler}
            onSaveHandler={onSaveHandler}
            saveState={saveState}
            testState={testState}
            deleteState={deleteState}
            setValidationError={setValidationError}
            validationError={validationError}
            testResults={testResults}
            onChange={onChange}
            hasErrors={hasErrors}
            errors={errors}
            settings={settings}
        />
    ) : (
        <GrammarPageVerstehen
            onShow={onIntentHandler}
            skillId={skillId}
            intentSettings={settings}
            entities={entities}
            setIsChanged={setIsChanged}
            onClose={onClose}
        />
    );
};
